import { useMemo } from 'react'

/**
 * Analyzes social media data and returns actionable insights for a creator.
 * Designed to help creators improve their content strategy.
 */
export default function CreatorInsights({ tiktokData, instaData, isMobile }) {
  const insights = useMemo(() => {
    const tips = []

    // ── TikTok Insights ─────────────────────────────────
    if (tiktokData?.length) {
      // 1. Best performing video duration
      const durationBuckets = { '0-15s': [], '15-30s': [], '30-60s': [], '60s+': [] }
      tiktokData.forEach(p => {
        const dur = p.videoMeta?.duration || 0
        if (dur <= 15) durationBuckets['0-15s'].push(p)
        else if (dur <= 30) durationBuckets['15-30s'].push(p)
        else if (dur <= 60) durationBuckets['30-60s'].push(p)
        else durationBuckets['60s+'].push(p)
      })
      let bestDuration = null, bestAvg = 0
      Object.entries(durationBuckets).forEach(([range, posts]) => {
        if (posts.length >= 3) {
          const avg = posts.reduce((s, p) => s + (p.playCount || 0), 0) / posts.length
          if (avg > bestAvg) { bestAvg = avg; bestDuration = range }
        }
      })
      if (bestDuration) {
        const avgK = bestAvg >= 1e6 ? (bestAvg / 1e6).toFixed(1) + 'M' : (bestAvg / 1e3).toFixed(0) + 'K'
        tips.push({
          platform: 'tiktok',
          category: 'Content Length',
          icon: '⏱',
          title: `${bestDuration} videos perform best`,
          detail: `Your ${bestDuration} videos average ${avgK} views — focus on this format for maximum reach.`,
          priority: 'high',
        })
      }

      // 2. Engagement trend (last 3 months vs prior 3 months)
      const sorted = [...tiktokData].sort((a, b) => new Date(b.createTimeISO) - new Date(a.createTimeISO))
      const recent = sorted.slice(0, Math.ceil(sorted.length * 0.3))
      const older = sorted.slice(Math.ceil(sorted.length * 0.5))
      if (recent.length && older.length) {
        const recentEng = recent.reduce((s, p) => s + (p.diggCount || 0) + (p.shareCount || 0) + (p.commentCount || 0), 0) / recent.length
        const olderEng = older.reduce((s, p) => s + (p.diggCount || 0) + (p.shareCount || 0) + (p.commentCount || 0), 0) / older.length
        const change = olderEng > 0 ? ((recentEng - olderEng) / olderEng * 100) : 0
        if (Math.abs(change) > 10) {
          tips.push({
            platform: 'tiktok',
            category: 'Engagement Trend',
            icon: change > 0 ? '📈' : '📉',
            title: `Engagement is ${change > 0 ? 'up' : 'down'} ${Math.abs(change).toFixed(0)}%`,
            detail: change > 0
              ? `Recent posts are getting more engagement — keep doing what you're doing! Analyze your latest hits for repeatable patterns.`
              : `Recent posts are underperforming compared to earlier content. Consider revisiting the style, topics, or hooks from your top posts.`,
            priority: change > 0 ? 'success' : 'warning',
          })
        }
      }

      // 3. Best posting day of week
      const dayMap = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' }
      const byDay = {}
      tiktokData.forEach(p => {
        const day = new Date(p.createTimeISO).getDay()
        if (!byDay[day]) byDay[day] = { views: 0, count: 0 }
        byDay[day].views += p.playCount || 0
        byDay[day].count++
      })
      let bestDay = null, bestDayAvg = 0
      Object.entries(byDay).forEach(([day, data]) => {
        if (data.count >= 3) {
          const avg = data.views / data.count
          if (avg > bestDayAvg) { bestDayAvg = avg; bestDay = dayMap[day] }
        }
      })
      if (bestDay) {
        const avgK = bestDayAvg >= 1e6 ? (bestDayAvg / 1e6).toFixed(1) + 'M' : (bestDayAvg / 1e3).toFixed(0) + 'K'
        tips.push({
          platform: 'tiktok',
          category: 'Best Day',
          icon: '📅',
          title: `Post on ${bestDay}s for best results`,
          detail: `Your ${bestDay} posts average ${avgK} views — significantly higher than other days.`,
          priority: 'high',
        })
      }

      // 4. Virality ratio -- shares vs views
      const totalViews = tiktokData.reduce((s, p) => s + (p.playCount || 0), 0)
      const totalShares = tiktokData.reduce((s, p) => s + (p.shareCount || 0), 0)
      const shareRate = totalViews > 0 ? (totalShares / totalViews * 100) : 0
      if (shareRate < 0.2) {
        tips.push({
          platform: 'tiktok',
          category: 'Shareability',
          icon: '🔄',
          title: 'Low share rate — add stronger CTAs',
          detail: `Only ${shareRate.toFixed(2)}% of viewers share your content. Try adding "Send this to someone who..." or relatable hooks that prompt sharing.`,
          priority: 'medium',
        })
      } else if (shareRate > 0.5) {
        tips.push({
          platform: 'tiktok',
          category: 'Shareability',
          icon: '🚀',
          title: 'Great share rate!',
          detail: `${shareRate.toFixed(2)}% share rate is strong — your content resonates enough to spread organically. Keep creating relatable, share-worthy content.`,
          priority: 'success',
        })
      }

      // 5. Bookmark insights
      const totalBookmarks = tiktokData.reduce((s, p) => s + (p.collectCount || 0), 0)
      const bookmarkRate = totalViews > 0 ? (totalBookmarks / totalViews * 100) : 0
      if (bookmarkRate > 0.3) {
        tips.push({
          platform: 'tiktok',
          category: 'Save Rate',
          icon: '🔖',
          title: 'High bookmark rate — create more "save-worthy" content',
          detail: `${bookmarkRate.toFixed(2)}% of viewers save your content. This signals educational/useful value. Consider doing more tutorials, tips, or how-tos.`,
          priority: 'success',
        })
      }

      // 6. Posting consistency
      const byMonth = {}
      tiktokData.forEach(p => {
        const d = new Date(p.createTimeISO)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        byMonth[key] = (byMonth[key] || 0) + 1
      })
      const monthCounts = Object.values(byMonth)
      const avgPostsPerMonth = monthCounts.length ? monthCounts.reduce((a, b) => a + b, 0) / monthCounts.length : 0
      const latestMonths = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 3)
      const recentAvg = latestMonths.length ? latestMonths.reduce((s, [, c]) => s + c, 0) / latestMonths.length : 0
      if (recentAvg < avgPostsPerMonth * 0.7 && avgPostsPerMonth > 3) {
        tips.push({
          platform: 'tiktok',
          category: 'Consistency',
          icon: '📊',
          title: 'Posting frequency has dropped',
          detail: `You averaged ${avgPostsPerMonth.toFixed(0)} posts/month overall but only ${recentAvg.toFixed(0)} recently. Consistent posting helps the algorithm surface your content.`,
          priority: 'warning',
        })
      }

      // 7. Top content themes (from text)
      const wordFreq = {}
      const stopWords = new Set(['the','a','an','de','le','la','les','des','du','un','une','je','tu','il','elle','on','and','is','it','to','for','in','with','my','this','that','you','me','i','of','et','en','au','ma','mon','mes','ce','ces','pour','qui','que','est','pas','plus','sur','dans','avec'])
      tiktokData.forEach(p => {
        (p.text || '').toLowerCase().replace(/[^a-zéèêëàâäùûüîïôöç\s]/g, '').split(/\s+/).forEach(w => {
          if (w.length > 3 && !stopWords.has(w)) wordFreq[w] = (wordFreq[w] || 0) + 1
        })
      })
      const topWords = Object.entries(wordFreq).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([w]) => w)
      if (topWords.length >= 3) {
        tips.push({
          platform: 'tiktok',
          category: 'Content Themes',
          icon: '💡',
          title: `Top themes: ${topWords.slice(0, 3).join(', ')}`,
          detail: `These words appear most in your content. Consider doubling down on related topics or branching into adjacent niches for growth.`,
          priority: 'info',
        })
      }
    }

    // ── Instagram Insights ─────────────────────────────────
    if (instaData?.length) {
      // 1. Best content type
      const byType = {}
      instaData.forEach(p => {
        const t = p.type || 'Unknown'
        if (!byType[t]) byType[t] = { likes: 0, count: 0 }
        byType[t].likes += p.likesCount || 0
        byType[t].count++
      })
      let bestType = null, bestTypeAvg = 0
      Object.entries(byType).forEach(([type, data]) => {
        if (data.count >= 2) {
          const avg = data.likes / data.count
          if (avg > bestTypeAvg) { bestTypeAvg = avg; bestType = type }
        }
      })
      if (bestType) {
        const fmtN = n => n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : n
        tips.push({
          platform: 'instagram',
          category: 'Content Format',
          icon: '🎯',
          title: `${bestType} posts get the most engagement`,
          detail: `Your ${bestType} posts average ${fmtN(Math.round(bestTypeAvg))} likes vs other formats. Prioritize this format in your content calendar.`,
          priority: 'high',
        })
      }

      // 2. Comments-to-likes ratio
      const totalLikes = instaData.reduce((s, p) => s + (p.likesCount || 0), 0)
      const totalComments = instaData.reduce((s, p) => s + (p.commentsCount || 0), 0)
      const commentRate = totalLikes > 0 ? (totalComments / totalLikes * 100) : 0
      if (commentRate < 3) {
        tips.push({
          platform: 'instagram',
          category: 'Community',
          icon: '💬',
          title: 'Low comment ratio — boost conversation',
          detail: `Only ${commentRate.toFixed(1)}% of likes convert to comments. Try asking questions in captions, using polls in stories, or creating debate-worthy content.`,
          priority: 'medium',
        })
      } else if (commentRate > 7) {
        tips.push({
          platform: 'instagram',
          category: 'Community',
          icon: '🔥',
          title: 'Strong community engagement',
          detail: `${commentRate.toFixed(1)}% comment-to-like ratio is excellent! Your audience is invested. Keep fostering these conversations.`,
          priority: 'success',
        })
      }

      // 3. Hashtag performance
      const hashtagMap = {}
      instaData.forEach(p => {
        (p.hashtags || []).forEach(h => {
          if (!hashtagMap[h]) hashtagMap[h] = { count: 0, totalLikes: 0 }
          hashtagMap[h].count++
          hashtagMap[h].totalLikes += p.likesCount || 0
        })
      })
      const hashArr = Object.entries(hashtagMap).map(([tag, d]) => ({ tag, ...d, avgLikes: d.totalLikes / d.count }))
      const topHash = hashArr.sort((a, b) => b.avgLikes - a.avgLikes).slice(0, 3)
      const weakHash = hashArr.filter(h => h.count >= 2).sort((a, b) => a.avgLikes - b.avgLikes).slice(0, 3)
      if (topHash.length) {
        const fmtN = n => n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : n
        tips.push({
          platform: 'instagram',
          category: 'Top Hashtags',
          icon: '#️⃣',
          title: `Best hashtags: ${topHash.map(h => '#' + h.tag).join(', ')}`,
          detail: `These hashtags correlate with your highest engagement (${fmtN(Math.round(topHash[0].avgLikes))} avg likes). Use them consistently.`,
          priority: 'high',
        })
      }
      if (weakHash.length && weakHash[0].avgLikes < (totalLikes / instaData.length) * 0.5) {
        tips.push({
          platform: 'instagram',
          category: 'Weak Hashtags',
          icon: '🏷️',
          title: `Consider dropping: ${weakHash.map(h => '#' + h.tag).join(', ')}`,
          detail: `These hashtags are associated with below-average performance. Try replacing them with more targeted or trending alternatives.`,
          priority: 'medium',
        })
      }

      // 4. Best posting day for IG
      const dayMap = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' }
      const byDay = {}
      instaData.forEach(p => {
        const d = new Date(p.timestamp)
        if (isNaN(d.getTime())) return
        const day = d.getDay()
        if (!byDay[day]) byDay[day] = { likes: 0, count: 0 }
        byDay[day].likes += p.likesCount || 0
        byDay[day].count++
      })
      let bestDay = null, bestDayAvg = 0
      Object.entries(byDay).forEach(([day, data]) => {
        if (data.count >= 2) {
          const avg = data.likes / data.count
          if (avg > bestDayAvg) { bestDayAvg = avg; bestDay = dayMap[day] }
        }
      })
      if (bestDay) {
        const fmtN = n => n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : n
        tips.push({
          platform: 'instagram',
          category: 'Best Day',
          icon: '📅',
          title: `${bestDay}s get the most engagement`,
          detail: `Your ${bestDay} posts average ${fmtN(Math.round(bestDayAvg))} likes. Schedule your best content for this day.`,
          priority: 'high',
        })
      }
    }

    // Sort: high priority first, then success, medium, warning, info
    const priorityOrder = { high: 0, success: 1, warning: 2, medium: 3, info: 4 }
    tips.sort((a, b) => (priorityOrder[a.priority] || 5) - (priorityOrder[b.priority] || 5))

    return tips
  }, [tiktokData, instaData])

  if (!insights.length) return null

  const priorityColors = {
    high: { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.25)', text: '#f97316' },
    success: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', text: '#10b981' },
    warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', text: '#f59e0b' },
    medium: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', text: '#3b82f6' },
    info: { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.25)', text: '#8b5cf6' },
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
        Creator Insights
      </div>
      <p style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 14 }}>
        Actionable recommendations based on {(tiktokData?.length || 0) + (instaData?.length || 0)} posts analyzed
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
        {insights.map((tip, i) => {
          const colors = priorityColors[tip.priority] || priorityColors.info
          return (
            <div key={i} style={{
              background: colors.bg, border: `1px solid ${colors.border}`,
              borderRadius: 'var(--radius-lg, 12px)', padding: '14px 16px',
              transition: 'transform 0.15s ease',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = ''}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 18 }}>{tip.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '2px 6px', borderRadius: 4,
                      background: tip.platform === 'tiktok' ? 'rgba(0,0,0,0.3)' : 'rgba(131,58,180,0.15)',
                      color: tip.platform === 'tiktok' ? '#fff' : '#c13584'
                    }}>
                      {tip.platform === 'tiktok' ? 'TT' : 'IG'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {tip.category}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: colors.text, marginTop: 2 }}>
                    {tip.title}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, paddingLeft: 26 }}>
                {tip.detail}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
